import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  RefreshCw,
  Plus,
  Flag,
  ToggleLeft,
  ToggleRight,
  Percent,
  Clock,
  User,
  Settings,
  Trash2,
  Edit
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnerAdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, flag: null });
  
  // Form states
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newEnabled, setNewEnabled] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [editRollout, setEditRollout] = useState([100]);
  const [submitting, setSubmitting] = useState(false);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/feature-flags`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFlags(data.flags || []);
      }
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a flag name');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/feature-flags?name=${encodeURIComponent(newName)}&description=${encodeURIComponent(newDescription)}&enabled=${newEnabled}`, {
          credentials: 'include',
          method: 'POST'
        }
      );
      
      if (response.ok) {
        toast.success('Feature flag created');
        setCreateDialog(false);
        setNewName('');
        setNewDescription('');
        setNewEnabled(false);
        fetchFlags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create flag');
      }
    } catch (error) {
      toast.error('Failed to create flag');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.flag) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/feature-flags/${editDialog.flag.flag_id}?enabled=${editEnabled}&rollout_percentage=${editRollout[0]}`, {
          credentials: 'include',
          method: 'PUT'
        }
      );
      
      if (response.ok) {
        toast.success('Feature flag updated');
        setEditDialog({ open: false, flag: null });
        fetchFlags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update flag');
      }
    } catch (error) {
      toast.error('Failed to update flag');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (flag) => {
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/feature-flags/${flag.flag_id}?enabled=${!flag.enabled}`, {
          credentials: 'include',
          method: 'PUT'
        }
      );
      
      if (response.ok) {
        toast.success(`Flag ${!flag.enabled ? 'enabled' : 'disabled'}`);
        fetchFlags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle flag');
      }
    } catch (error) {
      toast.error('Failed to toggle flag');
    }
  };

  const openEditDialog = (flag) => {
    setEditEnabled(flag.enabled);
    setEditRollout([flag.rollout_percentage || 100]);
    setEditDialog({ open: true, flag });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !flags.length) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48 bg-white/10" />
          <Skeleton className="h-10 w-32 bg-white/10" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Feature Flags</h1>
          <p className="text-muted-foreground">Control feature rollouts and experiments</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={fetchFlags} 
            className="border-white/20 text-muted-foreground/80"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setCreateDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Flag
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Flags</p>
                <p className="text-2xl font-bold text-white">{flags.length}</p>
              </div>
              <Flag className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Enabled</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {flags.filter(f => f.enabled).length}
                </p>
              </div>
              <ToggleRight className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Disabled</p>
                <p className="text-2xl font-bold text-muted-foreground">
                  {flags.filter(f => !f.enabled).length}
                </p>
              </div>
              <ToggleLeft className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Flags Grid */}
      {flags.length === 0 ? (
        <Card className="bg-card border-white/15">
          <CardContent className="py-12 text-center">
            <Flag className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">No feature flags created yet</p>
            <Button 
              variant="outline" 
              className="border-white/20"
              onClick={() => setCreateDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Flag
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {flags.map((flag) => (
            <Card 
              key={flag.flag_id} 
              className={`bg-card border-white/15 ${flag.enabled ? 'border-l-4 border-l-emerald-500' : ''}`}
              data-testid={`flag-card-${flag.flag_id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-white">{flag.name}</h3>
                      <Badge className={flag.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted0/20 text-muted-foreground'}>
                        {flag.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{flag.description || 'No description'}</p>
                  </div>
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={() => handleToggle(flag)}
                    className="data-[state=checked]:bg-emerald-600"
                  />
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-white/15">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Percent className="h-4 w-4" />
                      <span>{flag.rollout_percentage || 100}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatDate(flag.created_at)}</span>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => openEditDialog(flag)}
                    className="text-muted-foreground hover:text-white"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Configure
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Create Feature Flag</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add a new feature flag to control feature rollouts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Flag Name *</label>
              <Input
                placeholder="e.g., new_dashboard_ui"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
              <p className="text-xs text-muted-foreground mt-1">Use snake_case for consistency</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Description</label>
              <Textarea
                placeholder="What does this flag control?"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-white">Enable on creation</label>
                <p className="text-xs text-muted-foreground">Turn on immediately after creating</p>
              </div>
              <Switch
                checked={newEnabled}
                onCheckedChange={setNewEnabled}
                className="data-[state=checked]:bg-emerald-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !newName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Creating...' : 'Create Flag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, flag: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Configure Flag</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update settings for <strong className="text-white">{editDialog.flag?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm text-white">Enabled</label>
                <p className="text-xs text-muted-foreground">Turn this feature on/off</p>
              </div>
              <Switch
                checked={editEnabled}
                onCheckedChange={setEditEnabled}
                className="data-[state=checked]:bg-emerald-600"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-white">Rollout Percentage</label>
                <span className="text-sm text-muted-foreground">{editRollout[0]}%</span>
              </div>
              <Slider
                value={editRollout}
                onValueChange={setEditRollout}
                max={100}
                step={5}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Percentage of users who will see this feature when enabled
              </p>
            </div>
            
            {editDialog.flag && (
              <div className="pt-4 border-t border-white/15 text-sm text-muted-foreground">
                <p>Created: {formatDate(editDialog.flag.created_at)}</p>
                {editDialog.flag.updated_at && (
                  <p>Last updated: {formatDate(editDialog.flag.updated_at)}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, flag: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
